/** Historial de estados móvil: transiciones del documento en formato de tarjetas. */
import { CardListSkeleton } from './LoadingSkeleton';
import { EmptyState } from './EmptyState';
import { MobileIcon } from './MobileIcon';
import { SectionCard } from './SectionCard';
import { StatusBadge } from './StatusBadge';
import { useEntityStatusHistoryQuery, type HistoryEntityType } from '@/hooks/useMobileHistory';

const ACTION_LABELS: Record<string, string> = {
  create: 'Crear',
  update: 'Actualizar',
  submit_review: 'Enviar a revisión',
  observe: 'Observar',
  resolve_observations: 'Resolver observaciones',
  send_to_design: 'Enviar a diseño',
  send_to_budget: 'Enviar a presupuesto',
  complete: 'Completar',
  cancel: 'Cancelar',
  send_to_commercial: 'Enviar a comercial',
  request_workflow: 'Solicitar workflow',
  withdraw_request: 'Retirar solicitud',
  approve: 'Aprobar',
  reject: 'Rechazar',
  create_version: 'Nueva versión',
  generate_quotation: 'Generar cotización',
};

function actionLabel(action: string): string {
  if (ACTION_LABELS[action]) return ACTION_LABELS[action];
  const text = action.replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function StatusHistoryCard({
  entityType,
  entityId,
  enabled = true,
}: {
  entityType: HistoryEntityType;
  entityId: string | null;
  enabled?: boolean;
}) {
  const historyQuery = useEntityStatusHistoryQuery(entityType, entityId, enabled);
  const items = historyQuery.data ?? [];

  return (
    <SectionCard title={`Historial (${items.length})`}>
      {historyQuery.isPending && <CardListSkeleton count={2} />}
      {historyQuery.isError && (
        <EmptyState
          icon="error"
          title="No se pudo cargar el historial"
          description="Verifica tu conexión e intenta de nuevo."
          actionLabel="Reintentar"
          onAction={() => void historyQuery.refetch()}
        />
      )}
      {!historyQuery.isPending && !historyQuery.isError && items.length === 0 && (
        <div className="rounded-xl bg-bg-tertiary p-4 text-center">
          <p className="text-sm font-semibold text-text-primary">Sin transiciones registradas</p>
        </div>
      )}
      {!historyQuery.isPending && !historyQuery.isError && items.length > 0 && (
        <ul className="space-y-2">
          {items.map((item) => (
            <li key={item.id} className="rounded-xl bg-bg-tertiary px-4 py-3">
              <div className="flex items-start justify-between gap-2">
                <p className="min-w-0 flex-1 break-words text-sm font-semibold text-text-primary">
                  {actionLabel(item.action)}
                </p>
                <span className="shrink-0 text-xs tabular-nums text-text-tertiary">
                  {formatDateTime(item.changed_at)}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {item.from_status && <StatusBadge status={item.from_status} size="sm" />}
                <MobileIcon name="arrow_forward" className="h-4 w-4 shrink-0 text-text-tertiary" />
                <StatusBadge status={item.to_status} size="sm" />
              </div>
              <p className="mt-2 truncate text-xs text-text-secondary">
                {item.changed_by_name ?? 'Sistema'}
              </p>
              {item.reason && (
                <p className="mt-1 break-words text-xs text-text-secondary">{item.reason}</p>
              )}
              {item.comments && (
                <p className="mt-1 break-words text-xs text-text-tertiary">{item.comments}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
